import React, { useState } from 'react';
import { Stack, Text, IconButton } from '@fluentui/react';
import { 
  CopyRegular, 
  ChevronDownRegular, 
  ChevronUpRegular,
  CheckmarkRegular,
  WarningRegular,
  LightbulbRegular
} from '@fluentui/react-icons';
import styles from './FeatureCard.module.css';

interface QueryExample {
  query: { en: string; he: string };
  explanation: { en: string; he: string };
  expectedSource: string;
}

interface CommonMistake {
  wrong: { en: string; he: string };
  right: { en: string; he: string };
  explanation: { en: string; he: string };
}

interface Tip {
  en: string;
  he: string;
}

export interface Feature {
  id: string;
  category: 'source' | 'feature';
  title: { en: string; he: string };
  description: { en: string; he: string };
  icon: string;
  colorScheme: string;
  triggerWords: { en: string[]; he: string[] };
  examples: QueryExample[];
  commonMistakes: CommonMistake[];
  tips: Tip[];
  relatedFeatures: string[];
  popularity: number;
  lastUpdated: string;
}

interface FeatureCardProps {
  feature: Feature;
  isExpanded?: boolean;
  onToggleExpand?: (id: string) => void;
  searchHighlight?: string;
}

const COLOR_SCHEMES: Record<string, { primary: string; gradient: string; bgLight: string }> = {
  office365: {
    primary: '#0078D4',
    gradient: 'linear-gradient(135deg, #0078D4 0%, #1A86E0 100%)',
    bgLight: '#E8F4FD'
  },
  liveSearch: {
    primary: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
    bgLight: '#E8F5E9'
  },
  modelKnowledge: {
    primary: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #AB47BC 100%)',
    bgLight: '#F3E5F5'
  },
  excelAnalysis: {
    primary: '#217346',
    gradient: 'linear-gradient(135deg, #217346 0%, #2E8B57 100%)',
    bgLight: '#E8F5E9'
  },
  fileUpload: {
    primary: '#FF5722',
    gradient: 'linear-gradient(135deg, #FF5722 0%, #FF7043 100%)',
    bgLight: '#FBE9E7'
  },
  researchScope: {
    primary: '#673AB7',
    gradient: 'linear-gradient(135deg, #673AB7 0%, #7E57C2 100%)',
    bgLight: '#EDE7F6'
  },
  deepResearch: {
    primary: '#00BCD4',
    gradient: 'linear-gradient(135deg, #00BCD4 0%, #26C6DA 100%)',
    bgLight: '#E0F7FA'
  }
};

const FeatureCard: React.FC<FeatureCardProps> = ({ 
  feature, 
  isExpanded = false, 
  onToggleExpand,
  searchHighlight 
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const colorScheme = COLOR_SCHEMES[feature.colorScheme] || COLOR_SCHEMES.modelKnowledge;

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleToggle = () => {
    onToggleExpand?.(feature.id);
  };

  const highlightText = (text: string): React.ReactNode => {
    if (!searchHighlight) return text;
    const regex = new RegExp(`(${searchHighlight})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className={styles.highlight}>{part}</mark> : part
    );
  };

  return (
    <div 
      className={`${styles.card} ${isExpanded ? styles.expanded : ''}`}
      style={{ '--accent-color': colorScheme.primary, '--accent-light': colorScheme.bgLight } as React.CSSProperties}
    >
      {/* Card Header */}
      <div className={styles.header} onClick={handleToggle}>
        <div className={styles.iconWrapper} style={{ background: colorScheme.gradient }}>
          <span className={styles.icon}>{feature.icon}</span>
        </div>
        
        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <Text className={styles.title}>{highlightText(feature.title.en)}</Text>
            {feature.popularity >= 85 && (
              <span className={styles.popularBadge}>Popular</span>
            )}
          </div>
          <Text className={styles.description}>{highlightText(feature.description.en)}</Text>
        </div>

        <IconButton 
          className={styles.expandButton}
          ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
        </IconButton>
      </div>

      {/* Trigger Words Preview (collapsed only) */}
      {!isExpanded && feature.triggerWords.en.length > 0 && (
        <div className={styles.triggerPreview}>
          {feature.triggerWords.en.slice(0, 3).map((word, idx) => (
            <span key={idx} className={styles.triggerChip}>{word}</span>
          ))}
          {feature.triggerWords.en.length > 3 && (
            <span className={styles.moreChip}>+{feature.triggerWords.en.length - 3}</span>
          )}
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className={styles.expandedContent}>
          {/* Trigger Words Section */}
          {feature.triggerWords.en.length > 0 && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>🎯 Trigger Words</Text>
              <div className={styles.triggerGrid}>
                <div className={styles.triggerColumn}>
                  <Text className={styles.langLabel}>🇬🇧 English</Text>
                  <div className={styles.triggerList}>
                    {feature.triggerWords.en.map((word, idx) => (
                      <span key={idx} className={styles.triggerChip}>{word}</span>
                    ))}
                  </div>
                </div>
                <div className={styles.triggerColumn}>
                  <Text className={styles.langLabel}>🇮🇱 Hebrew</Text>
                  <div className={styles.triggerList}>
                    {feature.triggerWords.he.map((word, idx) => (
                      <span key={idx} className={styles.triggerChip}>{word}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Example Queries */}
          {feature.examples?.length > 0 && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>💬 Example Queries</Text>
              <Stack tokens={{ childrenGap: 12 }}>
                {feature.examples.map((example, idx) => (
                  <div key={idx} className={styles.exampleCard}>
                    <div className={styles.exampleRow}>
                      <span className={styles.langFlag}>🇬🇧</span>
                      <Text className={styles.queryText}>"{example.query.en}"</Text>
                      <IconButton
                        className={styles.copyButton}
                        onClick={() => handleCopy(example.query.en, idx)}
                        ariaLabel="Copy query"
                      >
                        {copiedIndex === idx ? <CheckmarkRegular /> : <CopyRegular />}
                      </IconButton>
                    </div>
                    <div className={styles.exampleRow}>
                      <span className={styles.langFlag}>🇮🇱</span>
                      <Text className={styles.queryText} dir="rtl">"{example.query.he}"</Text>
                      <IconButton
                        className={styles.copyButton}
                        onClick={() => handleCopy(example.query.he, idx + 100)}
                        ariaLabel="Copy query"
                      >
                        {copiedIndex === idx + 100 ? <CheckmarkRegular /> : <CopyRegular />}
                      </IconButton>
                    </div>
                    <Text className={styles.explanation}>{example.explanation.en}</Text>
                  </div>
                ))}
              </Stack>
            </div>
          )}

          {/* Common Mistakes */}
          {feature.commonMistakes?.length > 0 && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>
                <WarningRegular className={styles.warningIcon} /> Common Mistakes
              </Text>
              <Stack tokens={{ childrenGap: 8 }}>
                {feature.commonMistakes.map((mistake, idx) => (
                  <div key={idx} className={styles.mistakeCard}>
                    <div className={styles.mistakeRow}>
                      <span className={styles.wrongLabel}>❌</span>
                      <Text className={styles.wrongText}>"{mistake.wrong.en}"</Text>
                    </div>
                    <div className={styles.mistakeRow}>
                      <span className={styles.rightLabel}>✅</span>
                      <Text className={styles.rightText}>"{mistake.right.en}"</Text>
                    </div>
                    <Text className={styles.mistakeExplanation}>{mistake.explanation.en}</Text>
                  </div>
                ))}
              </Stack>
            </div>
          )}

          {/* Tips */}
          {feature.tips?.length > 0 && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>
                <LightbulbRegular className={styles.tipIcon} /> Pro Tips
              </Text>
              <ul className={styles.tipList}>
                {feature.tips.map((tip, idx) => (
                  <li key={idx} className={styles.tipItem}>{tip.en}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeatureCard;
